import { PlusOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts/colors"
import {
    LINE_ITEM_MATERIAL_CODE_MAX_LENGTH,
    LINE_ITEM_MATERIAL_CODE_MIN_LENGTH,
    LINE_ITEM_NAME_MAX_LENGTH,
    LINE_ITEM_NAME_MIN_LENGTH,
    LINE_ITEM_QUANTITY_MAX,
} from "@finway-group/shared/lib/models/purchaseOrder/lineItem.validation"
import { Button, Col, Form, Input, InputNumber, Row, Tooltip } from "antd"
import React, { Fragment } from "react"
import { useTranslation } from "react-i18next"
import { UnitOfMeasureSelect } from "~Components/form/UnitOfMeasureSelect"
import { LinkIcon } from "~Components/icons/LinkIcon"
import { TrashIcon } from "~Components/icons/TrashIcon"
import { createLineItemRules } from "~Features/pages/purchaseOrders/components/LineItem.rules"
import { DeliveryNoteFormValues } from "../types/deliveryNote.interfaces"
import { createDefaultLineItem } from "../utils/helpers.util"

interface LineItemsListProps {
    lineItems: DeliveryNoteFormValues["lineItems"]
}

export const LineItemsList = ({ lineItems }: LineItemsListProps) => {
    const { t } = useTranslation()
    const lineItemRules = createLineItemRules()

    return (
        <>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <div className="text-sm font-medium mb-4">Line Items</div>
                </Col>
            </Row>

            <Form.List name="lineItems" initialValue={lineItems?.length > 0 ? lineItems : [createDefaultLineItem()]}>
                {(fields, { add, remove }) => (
                    <>
                        <div className="line-item-card p-12">
                            <Row gutter={[12, 12]}>
                                {fields.map((field, idx) => {
                                    const lineItem = lineItems?.[idx]
                                    const isLinked = !!lineItem?.purchaseOrderLink

                                    return (
                                        <Fragment key={field.key}>
                                            <Col span={2}>
                                                <Form.Item>
                                                    <Input
                                                        className="border-0 text-center material-code-input rounded-full"
                                                        value={(idx + 1).toString().padStart(2, "0")}
                                                        disabled
                                                        readOnly
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item {...field} name={[field.name, "materialCode"]} rules={lineItemRules.materialCode}>
                                                    <Input
                                                        className="border-0 shadow-none outline-none rounded-full align-middle text-center h-34 material-code-input"
                                                        placeholder={t("placeholder:request.material_code")}
                                                        minLength={LINE_ITEM_MATERIAL_CODE_MIN_LENGTH}
                                                        maxLength={LINE_ITEM_MATERIAL_CODE_MAX_LENGTH}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item {...field} name={[field.name, "name"]} rules={lineItemRules.name} required>
                                                    <Input
                                                        prefix={<span style={{ color: "#FF0000" }}>*</span>}
                                                        className="border-none m-0 p-0"
                                                        placeholder={t("input:request.item_name")}
                                                        minLength={LINE_ITEM_NAME_MIN_LENGTH}
                                                        maxLength={LINE_ITEM_NAME_MAX_LENGTH}
                                                        required
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                                <Form.Item {...field} name={[field.name, "quantity"]} rules={lineItemRules.quantity}>
                                                    <InputNumber
                                                        type="number"
                                                        placeholder={t("purchaseOrders:drawer.quantity")}
                                                        min={0.01}
                                                        max={LINE_ITEM_QUANTITY_MAX}
                                                        disabled={isLinked}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item {...field} name={[field.name, "unitOfMeasure"]} rules={lineItemRules.unitOfMeasure}>
                                                    <UnitOfMeasureSelect />
                                                </Form.Item>
                                            </Col>
                                            <Col span={2}>
                                                <div className="flex w-full h-full align-middle justify-center">
                                                    {isLinked ? (
                                                        <Tooltip
                                                            title={t("label:po_link.item_linked_to_purchase_order", {
                                                                po_number: lineItem?.purchaseOrderLink?.purchaseOrderNumber,
                                                            })}
                                                        >
                                                            <LinkIcon color={COLOR.finway.green} />
                                                        </Tooltip>
                                                    ) : (
                                                        fields.length > 1 && (
                                                            <Button
                                                                type="text"
                                                                shape="circle"
                                                                icon={<TrashIcon />}
                                                                onClick={() => {
                                                                    remove(idx)
                                                                }}
                                                                className="cursor-pointer"
                                                                disabled={isLinked}
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            </Col>
                                        </Fragment>
                                    )
                                })}
                            </Row>
                        </div>

                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                const newItem = createDefaultLineItem()
                                add(newItem)
                            }}
                            className="font-normal text-[15px] leading-normal rounded-lg shadow-none flex items-center gap-2 p-[15px] mb-16 mt-8"
                            style={{ width: "fit-content" }}
                        >
                            {t("purchaseOrders:drawer.addItem")}
                        </Button>
                    </>
                )}
            </Form.List>
        </>
    )
}
